import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { interviewsAPI } from '../lib/api';
import { Card } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';

export default function InterviewResultsPage() {
  const { interviewId } = useParams();
  const navigate = useNavigate();
  const [interview, setInterview] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      const [iRes, sRes] = await Promise.all([
        interviewsAPI.get(interviewId),
        interviewsAPI.listSessions(interviewId)
      ]);
      setInterview(iRes.data);
      setSessions(sRes.data);
      if (sRes.data.length > 0) setSelected(sRes.data[0]);
    } catch (err) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, [interviewId]);

  useEffect(() => { loadData(); }, [loadData]);

  // Auto-refresh while scoring
  useEffect(() => {
    const scoring = sessions.some(s => ['submitted','scoring'].includes(s.status));
    if (!scoring) return;
    const iv = setInterval(async () => {
      try {
        const res = await interviewsAPI.listSessions(interviewId);
        setSessions(res.data);
        if (selected) { const u = res.data.find(s => s._id === selected._id); if (u) setSelected(u); }
      } catch(err){
        if (process.env.NODE_ENV === 'development') console.error('Poll sessions failed:', err);
      }
    }, 3000);
    return () => clearInterval(iv);
  }, [sessions, interviewId, selected]);

  const scoreColor = (s) => s >= 8 ? '#10B981' : s >= 6 ? '#0076A3' : s >= 4 ? '#F59E0B' : '#EF4444';

  if (loading) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'60vh',color:'hsl(var(--muted-foreground))' }}>Loading...</div>;
  if (!interview) return <div style={{ textAlign:'center',padding:'4rem',color:'hsl(var(--muted-foreground))' }}>Not found</div>;

  return (
    <div data-testid="interview-results-page">
      <div className="page-header">
        <div style={{ display:'flex',alignItems:'center',gap:'1rem' }}>
          <button onClick={() => navigate(-1)} style={{ background:'none',border:'none',color:'hsl(var(--muted-foreground))',cursor:'pointer',padding:'0.25rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          <div>
            <h1 className="page-title">{interview.template_name} Results</h1>
            <p className="page-subtitle">{sessions.length} session{sessions.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
      </div>

      {sessions.length === 0 ? (
        <div className="empty-state" style={{ paddingTop:'4rem' }}><p>No interview sessions yet.</p></div>
      ) : (
        <div style={{ display:'grid',gridTemplateColumns:'300px 1fr',gap:'1.5rem' }}>
          {/* Left: session list */}
          <div style={{ display:'flex',flexDirection:'column',gap:'0.5rem' }}>
            {sessions.map(s => (
              <Card key={s._id} onClick={() => setSelected(s)} style={{
                background:selected?._id===s._id?'rgba(30,104,255,0.08)':'hsl(var(--card))',
                border:`1px solid ${selected?._id===s._id?'rgba(30,104,255,0.3)':'hsl(var(--border))'}`,
                borderRadius:12,padding:'1rem',cursor:'pointer'
              }} className="candidate-row">
                <div style={{ display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'0.375rem' }}>
                  <span style={{ fontSize:'0.875rem',fontWeight:600 }}>{s.candidate_name}</span>
                  {s.summary?.overall_score != null ? (
                    <span style={{ fontFamily:'JetBrains Mono',fontSize:'0.875rem',fontWeight:600,color:scoreColor(s.summary.overall_score/10) }}>{s.summary.overall_score}%</span>
                  ) : <span style={{ fontSize:'0.6875rem',color:'#F59E0B' }}>{s.status}</span>}
                </div>
                <div style={{ fontSize:'0.6875rem',color:'hsl(var(--muted-foreground))' }}>{s.candidate_email}</div>
              </Card>
            ))}
          </div>

          {/* Right: detail */}
          {selected && (
            <div>
              {['submitted','scoring'].includes(selected.status) ? (
                <Card style={{ background:'rgba(245,158,11,0.08)',border:'1px solid rgba(245,158,11,0.2)',borderRadius:14,padding:'2rem',textAlign:'center' }}>
                  <span className="status-dot status-processing" style={{ display:'inline-block',marginBottom:'1rem' }} />
                  <h3 style={{ fontFamily:'Cabinet Grotesk',fontSize:'1.125rem',fontWeight:600,color:'#D97706' }}>AI Scoring in progress...</h3>
                  <p style={{ fontSize:'0.8125rem',color:'hsl(var(--muted-foreground))' }}>Evaluating interview responses.</p>
                </Card>
              ) : selected.status === 'scored' ? (
                <Tabs defaultValue="overview">
                  <TabsList style={{ background:'hsl(var(--card))',borderRadius:10,marginBottom:'1.25rem' }}>
                    <TabsTrigger value="overview" style={{ borderRadius:8 }}>Overview</TabsTrigger>
                    <TabsTrigger value="responses" style={{ borderRadius:8 }}>Responses ({(selected.scores||[]).length})</TabsTrigger>
                    <TabsTrigger value="integrity" style={{ borderRadius:8 }}>Integrity ({(selected.anti_cheat_events||[]).length})</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview">
                    <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:'1rem',marginBottom:'1.5rem' }}>
                      {[{l:'Overall',v:selected.summary?.overall_score},{l:'Communication',v:(selected.summary?.communication_score||0)*10},{l:'Technical Depth',v:(selected.summary?.technical_depth_score||0)*10}].map(({l,v}) => (
                        <Card key={l} style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:14,padding:'1.25rem',textAlign:'center' }}>
                          <div style={{ fontFamily:'Cabinet Grotesk',fontSize:'1.75rem',fontWeight:700,color:scoreColor((v||0)/10),marginBottom:'0.25rem' }}>{v||0}%</div>
                          <div style={{ fontSize:'0.75rem',color:'hsl(var(--muted-foreground))' }}>{l}</div>
                        </Card>
                      ))}
                    </div>

                    {selected.summary && (
                      <Card style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:14,padding:'1.5rem',marginBottom:'1rem' }}>
                        <h4 style={{ fontFamily:'Cabinet Grotesk',fontSize:'0.9375rem',fontWeight:600,marginBottom:'0.75rem' }}>AI Summary</h4>
                        <div style={{ display:'flex',alignItems:'center',gap:'0.5rem',marginBottom:'0.75rem' }}>
                          <Badge style={{ background:'rgba(30,104,255,0.15)',color:'#0076A3',border:'1px solid rgba(30,104,255,0.2)',borderRadius:999 }}>{selected.summary.overall_rating}</Badge>
                        </div>
                        <p style={{ fontSize:'0.875rem',color:'hsl(var(--muted-foreground))',lineHeight:1.7,marginBottom:'1rem' }}>{selected.summary.summary}</p>
                        {selected.summary.recommendation && (
                          <div style={{ padding:'0.75rem',borderRadius:10,background:'rgba(30,104,255,0.08)',border:'1px solid rgba(30,104,255,0.15)',fontSize:'0.8125rem' }}>
                            <strong style={{ color:'#0076A3' }}>Recommendation:</strong> {selected.summary.recommendation}
                          </div>
                        )}
                      </Card>
                    )}
                  </TabsContent>

                  <TabsContent value="responses">
                    <div style={{ display:'flex',flexDirection:'column',gap:'1rem' }}>
                      {(interview.questions||[]).map((q,i) => {
                        const sc = (selected.scores||[])[i]||{};
                        const tr = (selected.transcripts||[])[i]||'(No response)';
                        return (
                          <Card key={q.id || `q-${i}`} style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:12,padding:'1.25rem' }}>
                            <div style={{ display:'flex',justifyContent:'space-between',marginBottom:'0.5rem' }}>
                              <Badge style={{ background:'rgba(30,104,255,0.1)',color:'#0076A3',border:'1px solid rgba(30,104,255,0.2)',borderRadius:999,fontSize:'0.625rem' }}>{q.skill_tested}</Badge>
                              <span style={{ fontFamily:'JetBrains Mono',fontSize:'0.8125rem',fontWeight:600,color:scoreColor(sc.score||0) }}>{sc.score||'?'}/{sc.max_score||10}</span>
                            </div>
                            <p style={{ fontSize:'0.875rem',fontWeight:600,marginBottom:'0.5rem' }}>{q.question}</p>
                            <div style={{ padding:'0.75rem',borderRadius:8,background:'hsl(240 6% 7%)',fontSize:'0.8125rem',color:'hsl(var(--muted-foreground))',marginBottom:'0.5rem',whiteSpace:'pre-wrap',maxHeight:200,overflow:'auto' }}>{tr}</div>
                            {sc.feedback && <p style={{ fontSize:'0.75rem',color:'#A1A1AA',fontStyle:'italic' }}>{sc.feedback}</p>}
                          </Card>
                        );
                      })}
                    </div>
                  </TabsContent>

                  <TabsContent value="integrity">
                    <Card style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:14,padding:'1.5rem' }}>
                      <h4 style={{ fontFamily:'Cabinet Grotesk',fontSize:'0.9375rem',fontWeight:600,marginBottom:'1rem' }}>Anti-Cheat Log</h4>
                      {(selected.anti_cheat_events||[]).length === 0 ? (
                        <div style={{ textAlign:'center',padding:'2rem',color:'hsl(var(--muted-foreground))' }}>
                          <p style={{ color:'#10B981',fontWeight:600 }}>Clean interview</p>
                          <p style={{ fontSize:'0.8125rem' }}>No suspicious events.</p>
                        </div>
                      ) : (
                        <div style={{ display:'flex',flexDirection:'column',gap:'0.5rem' }}>
                          {(selected.anti_cheat_events||[]).map((e,i) => (
                            <div key={`cheat-${e.type}-${i}`} style={{ display:'flex',alignItems:'center',gap:'0.75rem',padding:'0.625rem 0.875rem',borderRadius:8,background:'hsl(240 6% 7%)',border:'1px solid hsl(var(--border))',fontSize:'0.8125rem' }}>
                              <Badge style={{ background:'rgba(239,68,68,0.15)',color:'#DC2626',border:'none',borderRadius:999,fontSize:'0.625rem' }}>{e.type?.replace('_',' ')}</Badge>
                              <span style={{ flex:1,color:'hsl(var(--muted-foreground))' }}>{e.details}</span>
                              <span style={{ fontSize:'0.6875rem',color:'hsl(var(--muted-foreground))' }}>Q{(e.question_index||0)+1}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </Card>
                  </TabsContent>
                </Tabs>
              ) : (
                <Card style={{ background:'hsl(var(--card))',border:'1px solid hsl(var(--border))',borderRadius:14,padding:'2rem' }}>
                  <p style={{ color:'hsl(var(--muted-foreground))' }}>Status: {selected.status}</p>
                </Card>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
